import { cast, flow, SnapshotIn, types } from 'mobx-state-tree'
import { SubscriptionApi } from '../../services/api-objects/SubscriptionApi'
import { SubscriptionPurchase } from './SubscriptionPurchase'
import { SubscriptionProduct } from './SubscriptionProduct'
import { PurchaseAttempt } from './PurchaseAttempt'
import { UserSubscription } from './UserSubscription'
import { UserSubscriptionDetail } from './UserSubscriptionDetail'
const _sortBy = require('lodash/sortBy')

export const SubscriptionStoreModel = types
  .model('SubscriptionStore')
  .props({
    status: types.optional(types.enumeration(['idle', 'pending', 'done', 'error']), 'idle'),
    selectedProduct: types.maybeNull(SubscriptionProduct),
    selectedPurchase: types.maybeNull(SubscriptionPurchase),
    userSubscriptions: types.array(UserSubscription),
    subscriptionLoadStatus: types.optional(
      types.enumeration(['idle', 'pending', 'done', 'error']),
      'idle',
    ),
    loadingPurchases: types.maybe(types.boolean),
    loadingProducts: types.maybe(types.boolean),
    selectedUserSubscription: types.maybeNull(UserSubscriptionDetail),
    products: types.array(SubscriptionProduct),
    purchases: types.array(SubscriptionPurchase),
    purchaseAttempts: types.array(PurchaseAttempt),
  })
  .views((self) => ({
    get sortedUserSubscriptions() {
      return _sortBy(
        self.userSubscriptions,
        [
          (item) => item.isExpired,
          (item) => new Date(item.expiration_date || new Date(8640000000000000)),
          (item) => item.title.toLowerCase(),
        ],
        ['asc', 'asc', 'asc'],
      )
    },
    get sortedAttempts() {
      return [...self.purchaseAttempts].sort(
        (a: any, b: any) =>
          new Date(b.attempt_timestamp).getTime() - new Date(a.attempt_timestamp).getTime(),
      )
    },
    get isLoadingSubscriptions() {
      return self.subscriptionLoadStatus === 'pending'
    },
    get hasActiveSubscriptions() {
      return self.userSubscriptions.some((s) => !s.isExpired)
    },
    get purchaseStats() {
      const active = self.purchases.filter((p) => p.isActive || p.isExpiring).length
      const expired = self.purchases.filter((p) => p.isExpired).length
      const failed = self.purchases.filter((p) => p.isFailed).length
      return { active, expired, failed, total: self.purchases.length }
    },
    get userSubscriptionContentIds() {
      const content = self.selectedUserSubscription?.product_content || []
      return {
        topic: content
          .filter((c: any) => c.object_type === 'Topic')
          .map((c: any) => c.content_user_id)
          .filter((c) => c !== undefined),
        path: content
          .filter((c: any) => c.object_type === 'Path')
          .map((c: any) => c.content_user_id)
          .filter((c) => c !== undefined),
        quiz: content
          .filter((c: any) => c.object_type === 'Quiz')
          .map((c: any) => c.content_user_id)
          .filter((c) => c !== undefined),
      }
    },
  }))
  .actions((self) => ({
    setStatus(value?: 'idle' | 'pending' | 'done' | 'error') {
      self.status = value || 'idle'
    },
    setSelectedProduct(product: SnapshotIn<typeof SubscriptionProduct> | null) {
      self.selectedProduct = product ? cast(product) : null
    },
    setSelectedPurchase(purchase: SnapshotIn<typeof SubscriptionPurchase> | null) {
      self.selectedPurchase = purchase ? cast(purchase) : null
    },
    setUserSubscriptions(subscriptions: SnapshotIn<typeof UserSubscription>[]) {
      self.userSubscriptions = cast(subscriptions)
    },
    getProducts: flow(function* getProducts() {
      self.status = 'pending'
      self.loadingProducts = true

      try {
        const response: any = yield SubscriptionApi.getProducts()
        if (response.ok && response.data) {
          const data = response.data.products.map((p: any) => ({
            ...p,
            content: (p.content || []).map((c: any) => ({
              ...c,
              id: Number(c.id),
              label: '',
              subLabel: '',
              checked: false,
            })),
          }))
          self.products = cast(data)
        }
        self.status = 'done'
        self.loadingProducts = false
      } catch (e) {
        self.status = 'error'
        self.loadingProducts = false

        console.error('getProducts error:', e)
      }
    }),
    getPurchases: flow(function* getPurchases(productId?: number, userId?: number) {
      self.status = 'pending'
      self.loadingPurchases = true
      try {
        // self.purchases.replace([])
        const response: any = yield SubscriptionApi.getPurchases(productId, userId)
        if (response.ok && response.data) {
          self.purchases = cast(response.data.purchases)
        }
        self.status = 'done'
        self.loadingPurchases = false
      } catch (e) {
        self.status = 'error'
        self.loadingPurchases = false

        console.error('getPurchases error:', e)
      }
    }),
    getSubscriptionsForUser: flow(function* getSubscriptionsForUser(userId: number) {
      self.subscriptionLoadStatus = 'pending'
      try {
        const response: any = yield SubscriptionApi.getUserSubscriptions(userId)
        if (response.ok && response.data) {
          self.userSubscriptions = cast(response.data.purchases)
        }
        self.subscriptionLoadStatus = 'done'
      } catch (e) {
        self.subscriptionLoadStatus = 'error'
        console.error('getSubscriptionsForUser error:', e)
      }
    }),
    createProduct: flow(function* createProduct(payload: any) {
      self.status = 'pending'
      try {
        yield SubscriptionApi.createProduct(payload)
        self.status = 'done'
        return true
      } catch (e) {
        self.status = 'error'
        console.error('createProduct error:', e)
        return false
      }
    }),
    archiveProducts: flow(function* archiveProducts(ids: number[]) {
      self.status = 'pending'
      try {
        yield SubscriptionApi.archiveProducts(ids)
        self.status = 'done'
        return true
      } catch (e) {
        self.status = 'error'
        console.error('deleteProducts error:', e)
        return false
      }
    }),
    extendPurchases: flow(function* extendProduct(ids: number[], endDateTime: string) {
      self.status = 'pending'
      try {
        yield SubscriptionApi.extendPurchases(ids, endDateTime)
        self.status = 'done'
        return true
      } catch (e) {
        self.status = 'error'
        console.error('extendProduct error:', e)
        return false
      }
    }),
    getUserSubscriptionDetail: flow(function* getUserSubscriptionDetail(
      user_id: number,
      purchase_id: number,
    ) {
      self.status = 'pending'
      try {
        const response: any = yield SubscriptionApi.getUserSubscriptionDetail(user_id, purchase_id)
        if (response.ok && response.data) {
          self.selectedUserSubscription = cast(response.data)
        }
        self.status = 'done'
        return true
      } catch (e) {
        self.status = 'error'
        console.error('getUserSubscriptionDetail error:', e)
        return false
      }
    }),
    getPurchase: flow(function* getPurchase(id: number) {
      self.status = 'pending'
      try {
        const response: any = yield SubscriptionApi.getPurchase(id)
        if (response.ok) {
          self.selectedPurchase = cast(response.data)
        }
        self.status = 'done'
      } catch (e) {
        self.status = 'error'
        console.error('getPurchase error:', e)
      }
    }),
    updateProduct: flow(function* updateProduct(id, payload) {
      self.status = 'pending'
      try {
        yield SubscriptionApi.updateProduct(id, payload)
        self.status = 'done'
        return true
      } catch (e) {
        console.error('getProduct error:', e)
        self.status = 'error'
        return false
      }
    }),
    getProduct: flow(function* getProduct(id: number) {
      self.status = 'pending'
      try {
        const response: any = yield SubscriptionApi.getProduct(id)
        if (response.ok) {
          const data = { ...response.data }
          data.content = (data.content || []).map((c: any) => ({
            ...c,
            id: Number(c.id),
            label: '',
            subLabel: '',
            checked: false,
          }))
          self.selectedProduct = cast(data)
        }
        self.status = 'done'
      } catch (e) {
        self.status = 'error'
        console.error('getProduct error:', e)
      }
    }),
    revokeSubscription: flow(function* revokeSubscription(id: number) {
      self.status = 'pending'
      try {
        yield SubscriptionApi.revokeSubscription(id)
        self.status = 'done'
        return true
      } catch (e) {
        self.status = 'error'
        console.error('endPurchases error:', e)
        return false
      }
    }),
    getPurchaseAttempts: flow(function* getPurchaseAttempts(userId: number, purchaseId: number) {
      try {
        const response: any = yield SubscriptionApi.getPurchaseAttempts(userId, purchaseId)
        if (response.ok && response.data) {
          self.purchaseAttempts = cast(response.data.attempts)
        }
      } catch (e) {
        console.error('getPurchaseAttempts error:', e)
      }
    }),
    reattemptPurchase: flow(function* reattemptPurchase(id: number) {
      self.status = 'pending'
      try {
        yield SubscriptionApi.reattemptPurchase(id)
        self.status = 'done'
        return true
      } catch (e) {
        self.status = 'error'
        console.error('reattemptPurchase error:', e)
        return false
      }
    }),
    testSubscription: flow(function* testSubscription(
      productIds: number[],
      userIds: number[],
      emails: string[],
      duration_days: number | null,
    ) {
      self.status = 'pending'
      try {
        yield SubscriptionApi.testSubscription(productIds, userIds, emails, duration_days)
        self.status = 'done'
        return true
      } catch (e) {
        self.status = 'error'
        console.error('giftSubscription error:', e)
        return false
      }
    }),
    directInvite: flow(function* directInvite(
      productIds: number[],
      emails: string[],
      duration_days: number | null,
    ) {
      self.status = 'pending'
      try {
        yield SubscriptionApi.directInvite(productIds, emails, duration_days)
        self.status = 'done'
        return true
      } catch (e) {
        self.status = 'error'
        console.error('directInvite error:', e)
        return false
      }
    }),
    ahaWebhook: flow(function* ahaWebhook(payload: any) {
      self.status = 'pending'
      try {
        yield SubscriptionApi.ahaWebhook(payload)
        self.status = 'done'
        return true
      } catch (e) {
        self.status = 'error'
        console.error('ahaWebhook error:', e)
        return false
      }
    }),
  }))
